import React, { useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { LoginCallback, useOktaAuth } from '@okta/okta-react';
import "./App.scss";

import Home from "./components/home";
import Favourites from "./components/favourites";
import Navbar from "./components/navbar";
import Partners from "./components/partners";
import Dashboard from "./components/user";
import FeedHomePage from "./components/rss/FeedHomePage";
import FeedReadPage from "./components/rss/FeedReadPage";
import ProfilePage from "./components/auth/ProfilePage";
import LoginPage from "./components/auth/LoginPage";
import RegistrationForm from "./components/auth/RegistrationForm";
import RequiredAuth from "./components/auth/RequiredAuth";
import AuthErrorCallback from "./components/auth/AuthErrorCallback";
import AuthLoadingCallback from "./components/auth/AuthLoadingCallback";
import oktaConfig from "./app.config";

// Import new ecosystem pages
import CDPage from "./components/ecosystem/CD";
import PCDPage from "./components/ecosystem/PCD";
import DSVPage from "./components/ecosystem/DSV";
import SERPage from "./components/ecosystem/SER";
import SystemMonitorPage from "./components/ecosystem/SystemMonitorPage";
import Footer from "./components/shared/Footer";
import NotFound from "./components/notfound";
import WhatsNewDialog from "./ui/WhatsNewDialog";

import { trackPageView, updateTrackingContext } from './services/googleServices';

const App = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const location = useLocation();
  const [isMounted] = React.useState(true);
  const [healthCards, setHealthCards] = useState([]);

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isPcdSubdomain = /myclouddashboard/i.test(hostname);
  const isCdSubdomain = /(^|\.)clouddashboard\./i.test(hostname) && !isPcdSubdomain;

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const titleEl = document.getElementById('hero-card-title');
    const descEl = document.getElementById('hero-card-desc');
    if (!titleEl || !descEl) return;

    const path = location.pathname.toLowerCase();
    const isPcdContext = isPcdSubdomain || path.includes('/pcd') || path.includes('/profile') || path.includes('/user') || authState?.isAuthenticated;

    if (isPcdContext) {
      titleEl.innerText = "HealthDash | Beer Bank | The Personalized Cloud Dashboard (PCD)";
      descEl.innerText = "HealthDash | Beer Bank | The Personalized Cloud Dashboard is an Website App, that enables to view and synchronize fitness data from and to 3rd party services like Huawei Health, Samsung Health and others the sleep, activity (like walking) and overall readiness (like relaxation metrics) and uses Okta (or Google Sign-In) to authenticate users and enable secure access of the data.";
    } else if (path.includes('/dsv')) {
      titleEl.innerText = "HealthDash | Data Structure Visualizer (DSV)";
      descEl.innerText = "Real-time visual data structure generation and health topology mapping interface for the HealthDash ecosystem.";
    } else if (path.includes('/ser')) {
      titleEl.innerText = "HealthDash | Synchronization Error Reporting (SER)";
      descEl.innerText = "Synchronization Error Reporting (SER) provides a comprehensive knowledge base of platform error codes and synchronization diagnostics for the HealthDash ecosystem.";
    } else {
      titleEl.innerText = "HealthDash | Beer Bank | The Cloud Dashboard";
      descEl.innerText = "HealthDash | Beer Bank | The Cloud Dashboard is an Website App, that enables to view and synchronize fitness data from and to 3rd party services like Huawei Health, Samsung Health and others the sleep, activity (like walking) and overall readiness (like relaxation metrics) and uses Okta (or Google Sign-In) to authenticate users and enable secure access of the data.";
    }
  }, [location.pathname, isPcdSubdomain, authState?.isAuthenticated]);

  useEffect(() => {
    const fetchHealthCards = async () => {
      if (authState?.isAuthenticated) {
        try {
          const accessToken = oktaAuth.getAccessToken();
          const response = await fetch('/api/user/health-cards', {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });
          const data = await response.json();
          setHealthCards(data || []);
        } catch (err) {
          console.error("Failed to fetch health cards:", err);
        }
      }
    };
    fetchHealthCards();
  }, [authState, oktaAuth]);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
    updateTrackingContext({
      isAuthenticated: authState?.isAuthenticated,
      userTier: authState?.isAuthenticated ? 'Pro' : 'Standard'
    });
  }, [location, authState]);

  useEffect(() => {
    const loadModule = async () => {
      try {
        // Design-time bundled resolution for CD Foundation module with dynamic runtime fallback
        const baseModule = await import('@HolimaX/beerbank').catch(async () => {
          return await import(/* webpackIgnore: true */ '@HolimaX/beerbank').catch(() => null);
        });
        if (isMounted && baseModule && typeof baseModule.myFunction === 'function') {
          baseModule.myFunction({ authState });
        } else if (isMounted) {
          console.info("Optional CD base module not detected. Continuing with PCD-only capabilities.");
        }
      } catch (err) {}
    };
    loadModule();
  }, [isMounted, authState]);

  return (
    <div className="app-layout">
      <Navbar />
      <main className="app-main-content">
        <Routes>
          <Route 
            path="/" 
            element={
              new URLSearchParams(location.search).has('monitor') 
                ? <SystemMonitorPage /> 
                : isPcdSubdomain 
                  ? <PCDPage /> 
                  : isCdSubdomain 
                    ? <CDPage /> 
                    : <Home />
            } 
          />
          <Route path="/monitor" element={<SystemMonitorPage />} />
          <Route path="/login" element={<LoginPage baseUrl={oktaConfig.url} />} />
          <Route path="/register" element={<RegistrationForm />} />
          <Route 
            path="/implicit/callback" 
            element={
              <LoginCallback 
                errorComponent={AuthErrorCallback} 
                loadingElement={<AuthLoadingCallback />} 
              />
            } 
          />
          <Route path="/favourite" element={<Favourites />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/user" element={<Dashboard />} />
          <Route path="/profile" element={<ProfilePage healthCards={healthCards} />} />
          <Route path="/feed/*" element={
            <Routes>
              <Route path="feedhome" element={<FeedHomePage />} />
              <Route path="feedpage" element={<FeedReadPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          } />

          {/* --- New Ecosystem Routes --- */}
          <Route path="/ecosystem/CD" element={<CDPage />} />
          <Route path="/ecosystem/PCD" element={<PCDPage />} />
          <Route path="/ecosystem/DSV" element={<DSVPage />} />
          <Route path="/ecosystem/SER" element={<SERPage />} />
          <Route path="/ecosystem/monitor" element={<SystemMonitorPage />} />

          {/* --- 404 Not Found Catch-All Route --- */}
          <Route path="*" element={<NotFound />} />

        </Routes>
      </main>
      <Footer />
      <WhatsNewDialog />
    </div>
  );
};

export default App;
